/** Row cards for PipelineActivityTab. Pure components; helpers in
 *  activityHelpers.ts so fast-refresh stays clean.
 */
import { Link } from 'react-router-dom';
import { ArrowRight, RotateCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { STAGE_LABELS } from '@/types/enums';
import type { EscalationTask } from '@/pages/escalations/types';
import { pausedAt, relativeTime, type PausedJob } from './activityHelpers';

export function PausedJobCard({ job }: { job: PausedJob }) {
  const stage = pausedAt(job.review_status);
  const stageLabel = stage ? STAGE_LABELS[stage] : job.review_status;
  const isRecallable = job.recallable === true || job.status === 'cancelled';

  return (
    <Card className={isRecallable ? 'border-rose-300 dark:border-rose-800' : 'border-border'}>
      <CardContent className="pt-3 pb-3 px-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <Link
              to={`/jobs/${job.id}`}
              className="text-sm font-medium hover:underline truncate block"
              title={job.file_name ?? job.id}
            >
              {job.file_name ?? job.id}
            </Link>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
              {isRecallable ? (
                <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/30 dark:text-rose-200 dark:border-rose-900">
                  Cancelled — has progress
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                  Paused at {stageLabel}
                </Badge>
              )}
              <span>· {relativeTime(job.updated_at)}</span>
            </div>
          </div>
          <Button asChild size="sm" variant={isRecallable ? 'outline' : 'default'} className="gap-1.5">
            <Link to={`/jobs/${job.id}`}>
              {isRecallable ? <RotateCw className="h-3.5 w-3.5" aria-hidden="true" /> : null}
              {isRecallable ? 'Recall' : 'Resume'}
              {!isRecallable && <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />}
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function EscalationCard({ t }: { t: EscalationTask }) {
  return (
    <Card className="border-border">
      <CardContent className="pt-3 pb-3 px-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{t.comment}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
              <Badge variant="outline" className="text-[10px]">
                Stage: {t.stage}
              </Badge>
              <span>· {relativeTime(t.created_at)}</span>
            </div>
          </div>
          <Button asChild size="sm" variant="outline" className="gap-1.5">
            <Link to={`/jobs/${t.job_id}`}>
              Open
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
